import React, { useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import {
  Container, Row, Col, Card, CardBody,
} from 'reactstrap';

import { Button, ButtonLink } from 'components/Button';
import {
  confirmSignUpCode, resendSignUp, isValidCode, resolveErrorMessage,
} from 'utils/aws';
import { signIn as signInAction } from 'actions/auth';
import CodeInput from './components/CodeInput';
import { useResend } from './components/ResendControl';
import './style.scss';

const EmailVerification = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const email = get(location, 'state.email', 'your email');
  const username = get(location, 'state.userName', '');
  const password = get(location, 'state.password', '');
  const ssn = get(location, 'state.ssn', '');

  const [code, setCode] = useState('');
  const [hasError, setHasError] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isResending, setIsResending] = useState(false);

  // Resend cooldown / attempt-cap state machine (Req 2.7, 2.8, 11.5).
  // The actual Cognito resendSignUpCode call is owned here; a failure resets the
  // cooldown by invoking onFailure (which the hook also derives from a rejected
  // promise) so the user can retry immediately (Req 2.8, 11.4).
  const onResend = useCallback(({ onFailure }) => {
    if (!username) {
      toast.error('Your session expired. Please start registration again.');
      onFailure();
      navigate('/registration');
      return undefined;
    }
    setIsResending(true);
    return resendSignUp(username, { ssn, email })
      .then(() => {
        setIsResending(false);
        toast.success('A new verification code has been sent.');
      })
      .catch((err) => {
        setIsResending(false);
        onFailure();
        toast.error(err?.message || 'Could not resend the code. Please try again.');
      });
  }, [username, ssn, email, navigate]);

  const {
    remainingSeconds,
    disabled: resendCooldownDisabled,
    attemptsExhausted,
    requestResend,
  } = useResend(onResend);

  // Disable resend while any request is in flight (Req 2.6) in addition to the
  // cooldown / attempt-cap state (Req 2.7, 11.5).
  const resendDisabled = resendCooldownDisabled || isConfirming || isResending;

  const handleVerify = (e) => {
    e.preventDefault();
    // 6-digit numeric validation before calling Cognito (Req 2.1, 2.2).
    if (!isValidCode(code)) {
      setHasError(true);
      return;
    }
    // Missing-username guard: session state was lost (Req 2.3).
    if (!username) {
      toast.error('Your session expired. Please start registration again.');
      navigate('/registration');
      return;
    }
    setIsConfirming(true);

    // Confirm the sign-up code, then sign the just-confirmed user in and route
    // them straight into two-factor setup. Requiring a manual re-sign-in here
    // left users stranded on this screen (they had no obvious way to proceed),
    // so we complete sign-in for them. When the pool forces TOTP setup, the
    // sign-in returns the authenticator setup details, which we hand to the
    // setup screen. If sign-in can't be completed automatically (e.g. we don't
    // have the password in state), we fall back to the success -> sign-in page.
    confirmSignUpCode({ username, code })
      .then(() => {
        if (!password) {
          // No password carried in route state: fall back to the confirmation
          // page, which links to sign-in.
          setIsConfirming(false);
          navigate('/two-factor/verification-success', { state: { channel: 'email' } });
          return;
        }

        // Sign the just-confirmed user in through the app's normal sign-in flow
        // (the auth saga) rather than a bare Amplify signIn. This persists the
        // Session_Contract and runs postAuthentication, which evaluates MFA and
        // routes the user (to forced authenticator setup when required). Doing
        // this via the saga is what leaves a real authenticated session in
        // place, so the subsequent setUpTOTP call is authorized. Signing in with
        // a bare helper here skipped persistence and left setUpTOTP unauthorized
        // (Cognito 400).
        dispatch(signInAction({
          data: { username, password },
          navigate,
          success: () => {
            setIsConfirming(false);
            navigate('/consumer/dashboard');
          },
          fail: () => {
            // The account IS verified; if sign-in couldn't complete here (e.g. a
            // pending challenge or transient error), send the user to the
            // confirmation page to sign in manually rather than blocking.
            setIsConfirming(false);
            navigate('/two-factor/verification-success', { state: { channel: 'email' } });
          },
        }));
      })
      .catch((err) => {
        setIsConfirming(false);

        // The account is already confirmed (e.g. the user re-entered this screen
        // or the confirmation already succeeded). Cognito rejects a second
        // confirmation with NotAuthorizedException / "Current status is
        // CONFIRMED"; retrying a code can never succeed, so route them to the
        // sign-in screen instead of showing an invalid-code error.
        const alreadyConfirmed = err?.name === 'NotAuthorizedException'
          || err?.code === 'NotAuthorizedException'
          || /current status is confirmed/i.test(err?.message || '');

        if (alreadyConfirmed) {
          toast.success('Your account is already verified. Please sign in.');
          navigate('/');
          return;
        }

        // Invalid / expired code: surface the error and allow re-entry (Req 2.5).
        setHasError(true);
        const msg = resolveErrorMessage(err, 'Invalid or expired code. Please try again.');
        toast.error(msg);
      });
  };

  let resendLabel;
  if (attemptsExhausted) {
    resendLabel = 'Maximum resend attempts reached';
  } else if (isResending) {
    resendLabel = 'Sending a new code…';
  } else if (remainingSeconds > 0) {
    resendLabel = `Resend available in ${remainingSeconds}s`;
  } else {
    resendLabel = "Didn't receive the code? Resend";
  }

  return (
    <div className="two-factor-page">
      <Container fluid>
        <Row>
          <Col>
            <Card className="tf-card">
              <CardBody className="p-4">
                <h3 className="tf-title text-center">Verify your account</h3>
                <hr className="tf-hr" />

                <p className="tf-subtitle text-center">
                  We sent a verification code to
                  {' '}
                  <strong>{email}</strong>
                  {' '}
                  Enter it below to confirm your account.
                </p>

                <p className="tf-subtitle text-center mt-2 mb-2">
                  After verification, we&apos;ll take you to set up your two-factor authentication.
                </p>

                <form onSubmit={handleVerify}>
                  <p className="tf-code-label text-center">Enter 6-digit verification code</p>
                  <CodeInput
                    value={code}
                    onChange={(val) => { setCode(val); setHasError(false); }}
                    hasError={hasError}
                    disabled={isConfirming}
                    autoFocus
                  />

                  <Button
                    type="submit"
                    color="primary"
                    className="w-100 mt-4"
                    isLoading={isConfirming}
                    isDisabled={isResending}
                    onClick={handleVerify}
                  >
                    Verify &amp; Continue
                  </Button>
                </form>

                <div className="text-center mt-3">
                  <ButtonLink
                    className="tf-link"
                    onClick={requestResend}
                    isDisabled={resendDisabled}
                  >
                    {resendLabel}
                  </ButtonLink>
                </div>

                <div className="text-center mt-1">
                  <ButtonLink className="tf-link" onClick={() => navigate('/')}>
                    &larr; Back to sign in
                  </ButtonLink>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default EmailVerification;
